import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Alerts } from '@poseidon/components/StrokeIcons';

import type { CampaignPerson, SenderCount } from './campaignOwner';
import { campaignOf, isOwnerMismatch, personName, sendsFromLine } from './campaignOwner';

type Props = {
  /** The campaign's owner. Unknown (still loading): the notice waits rather than guess. */
  owner: CampaignPerson | null;
  /** Who the people send from. Omitted when the action sends nothing (reject, remove). */
  senders?: readonly SenderCount[];
  /** The person you're acting as — the warning names them when the campaign isn't theirs. */
  acting: CampaignPerson | null;
  className?: string;
};

const avatarProps = (p: CampaignPerson) => ({
  userTeamName: p.name,
  userEmail: p.email ?? null,
  profilePicture: p.profilePicture,
});

/**
 * The lines a bulk confirm carries about where the people go: whose campaign, whose LinkedIn sends,
 * and — when the campaign isn't the acting person's — a warning that says so in their name.
 */
export function CampaignSendersNotice({ owner, senders, acting, className }: Props) {
  if (owner == null) return null;
  const mismatch = isOwnerMismatch(owner, acting?.userId);
  return (
    <div
      data-testid="campaign-senders-notice"
      data-mismatch={mismatch ? 'true' : 'false'}
      className={cn(
        'mt-3 flex flex-col gap-1.5 rounded-md border px-3 py-2 text-sm',
        mismatch
          ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100'
          : 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200',
        className,
      )}>
      <div className="flex items-center gap-2">
        <TeamUserAvatar userTeam={avatarProps(owner)} className="size-5 text-3xs" shape="circle" />
        <span>
          Into <strong className="font-semibold">{campaignOf(owner)}</strong>
        </span>
      </div>
      {senders != null && senders.length > 0 && (
        <div className="flex items-center gap-2" data-testid="campaign-senders-line">
          <div className="flex shrink-0 -space-x-1.5">
            {senders.slice(0, 3).map((s) => (
              <TeamUserAvatar
                key={s.person.userId}
                userTeam={avatarProps(s.person)}
                className="size-5 text-3xs ring-2 ring-white dark:ring-slate-800"
                shape="circle"
              />
            ))}
          </div>
          <span>Messages send from {sendsFromLine(senders)}</span>
        </div>
      )}
      {mismatch && acting != null && (
        <div className="flex items-start gap-2 font-medium" data-testid="campaign-owner-mismatch-warning">
          <Alerts.AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>You&apos;re acting as {personName(acting)} — this isn&apos;t their campaign.</span>
        </div>
      )}
    </div>
  );
}
